/**
 * Thinking Engine — the brain.
 *
 * Consumes ONLY: Wizard Brief + Research Brief + Style Genome + Generation
 * Constraints. Produces ONE thing: a Creative Plan — complete design reasoning,
 * with a WHY for every decision. Pure, deterministic, versioned, composable.
 *
 * It never sees the editor, canvas, Fabric, geometry, pixels, the compiler, or
 * rendering. Future OpenAI receives only the Creative Plan.
 */

import { PLAN_SCHEMA_ID, PLAN_VERSION, OFFER_LED_PURPOSES } from './thinkingSchema';
import { resolveGenome } from './resolveGenome';
import { STEPS } from './steps';
import { runSelfReview } from './selfReview';
import { computeConfidence } from './confidence';
import { list } from './reasoning';

/**
 * Produce a Creative Plan.
 * @param {Object} inputs
 * @param {Object} inputs.brief         Wizard brief (documentType, industry, category, purpose, audience, mood, premiumLevel…).
 * @param {Object} inputs.research      Research Brief (Phase 4).
 * @param {(string|Object)} inputs.genome  Preferred Style Genome (id or object).
 * @param {Object} [inputs.constraints] Generation Constraints (Phase 5).
 * @returns {Object} the Creative Plan.
 */
export const think = (inputs = {}) => {
  const brief = inputs.brief && typeof inputs.brief === 'object' ? inputs.brief : {};
  const research = inputs.research && typeof inputs.research === 'object' ? inputs.research : {};
  const constraints = inputs.constraints && typeof inputs.constraints === 'object' ? inputs.constraints : {};

  const resolved = resolveGenome(inputs.genome, research, constraints);

  const ctx = {
    brief,
    research,
    genome: resolved.genome,
    genomeSwapped: resolved.swapped,
    constraints,
    offerLed: OFFER_LED_PURPOSES.includes(brief.purpose),
    variationAxes: list(constraints.generationConstraints && constraints.generationConstraints.recommendedVariationAxes),
    decisions: {},
  };

  const decisions = {};
  const reasoningTrace = [];
  STEPS.forEach((step) => {
    const dec = step.run(ctx);
    decisions[step.key] = dec;
    ctx.decisions[step.key] = dec;
    reasoningTrace.push({ step: step.id, choice: dec.choice, why: dec.why });
  });

  const selfReview = runSelfReview(decisions);
  reasoningTrace.push({
    step: 'self-consistency-review',
    choice: selfReview.consistent ? 'consistent' : 'issues-found',
    why: selfReview.notes.join(' ') || 'The plan is internally consistent: language, hierarchy, colour, and cues reinforce one another.',
  });

  return {
    schema: PLAN_SCHEMA_ID,
    version: PLAN_VERSION,
    meta: {
      genome: resolved.genome.id,
      genomeSwapped: resolved.swapped,
      researchConfidence: typeof research.confidence === 'number' ? research.confidence : null,
      researchFreshness: research.freshness || null,
    },
    ...decisions,
    selfReview,
    confidence: computeConfidence(ctx, selfReview),
    reasoningTrace,
  };
};
